import { ActionFunctionArgs, json, LoaderFunctionArgs, redirect } from "@remix-run/cloudflare";
import { getDbFromContext } from "@/lib/db.service.server";
import { Link, useActionData, useLoaderData, useNavigation } from "@remix-run/react";
import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { ValidatedForm, validationError } from "remix-validated-form";
import { NewUser, users } from "@/lib/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import React, { useEffect, useState } from "react";
import { trackEvent } from "@/lib/noraService.server";
import { existsConfig, getConfig } from "@/lib/config.server";
import { v4 } from "uuid";
import { emailService } from "@/lib/email.service";
import { authSessionStorage, REF_KEY, refSessionStorage } from "@/lib/cookie.server";
import { AuthenticatedUser } from "@/lib/auth.server";
import { bcrypt } from "@/lib/bcryptService.server";
import { maxTrialCredits } from "@/lib/utils";
import { ExternalScriptsHandle } from "remix-utils/external-scripts";
import { zfd } from "zod-form-data";
import { turnstileValidation } from "@/lib/misc.server";
import { useHydrated } from "remix-utils/use-hydrated";

export const validator = withZod(
  z.object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email("Must be a valid email"),
    name: zfd.text(z.string().min(2)),
    password: z.string().min(8, { message: "Password is too short" }),
    "cf-turnstile-response": zfd.text()
  })
);

export let handle: ExternalScriptsHandle<typeof loader> = {
  scripts({ id, data, params, matches, location, parentsData }) {
    return [
      {
        src: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
        crossOrigin: "anonymous",
        async: true,
        defer: true
      }
    ];
  }
};

export async function loader({ request, context }: LoaderFunctionArgs) {
  const cookieHeader = request.headers.get("Cookie");
  const session = await refSessionStorage.getSession(cookieHeader);
  console.log(session.get(REF_KEY));

  return json({
    DEV: existsConfig(context, "DEV"),
    siteKey: getConfig(context, "TURNSTILE_SITE_KEY")
    // siteKey: "2x00000000000000000000AB" // always fail
  });
}

export async function action({ request, context }: ActionFunctionArgs) {
  const formData = await request.formData();
  const result = await validator.validate(formData);
  if (result.error) {
    return validationError(result.error);
  }
  const { name, email, password, "cf-turnstile-response": token } = result.data;

  const tsValid = await turnstileValidation(context, request, token);
  if (tsValid) {
    const db = getDbFromContext(context);
    const hashedPassword = bcrypt.hashSync(password);

    const cookie = request.headers.get("Cookie");
    const session = await refSessionStorage.getSession(cookie);

    const newUser: NewUser = {
      email: email.toLowerCase(),
      password: hashedPassword,
      freeTrialCredits: maxTrialCredits,
      name,
      emailConfirmKey: v4(),
      confirmed: existsConfig(context, "DEV")
    };
    if (session.has(REF_KEY)) newUser.ref = session.get(REF_KEY); // add ?ref=something into user

    try {
      const user = await db.insert(users).values(newUser).returning().get();

      if (newUser.confirmed) {
        const cookieHeader = request.headers.get("Cookie");
        const session = await authSessionStorage.getSession(cookieHeader);
        const authUser: AuthenticatedUser = {
          email: user.email,
          inTeamOfId: undefined,
          id: user.id,
          confirmed: user.confirmed
        };
        session.set("user", authUser);
        session.set("strategy", "form");

        return redirect("/app", {
          headers: {
            "Set-Cookie": await authSessionStorage.commitSession(session)
          }
        });
      } else {
        const emailRes = await emailService.sendTemplate(newUser.email, {
          action_url: `https://rank-hub.com/confirm-email/${newUser.email}/${newUser.emailConfirmKey}`,
          name
        }, "confirmation", context);

        const emailData = await emailRes.text();

        await trackEvent(context, {
          type: "signup",
          email: newUser.email,
          postmarkData: emailData,
          postmarkStatus: emailRes.status
        });
      }
      return redirect("/confirm-email?email=" + newUser.email);
    } catch (err: any) {
      console.log(err);
      if (err.cause.toString().includes("constraint failed")) {
        return validationError({
          fieldErrors: {
            email: "Email already has an account"
          }
        });
      } else {
        throw err;
      }
    }
  } else {
    return validationError({
      fieldErrors: {
        email: "There was an error with your signup due to the Turnstile (captcha) not checking out. Please clear your cache and try again."
      }
    });
  }
}

export default function Index() {
  const { DEV, siteKey } = useLoaderData<typeof loader>();
  const { state } = useNavigation();
  const actionData = useActionData<typeof action>();
  const hydState = useHydrated();
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);

  useEffect(() => {
    // @ts-ignore this is needed because turnstile global var is not typed
    if (hydState && turnstileLoaded) {
      // @ts-ignore this is needed because turnstile global var is not typed
      window.turnstile.render(".cf-turnstile", {
        sitekey: siteKey
      });
    }
  }, [hydState, turnstileLoaded]);

  useEffect(() => {
    const x = setInterval(() => {
      // @ts-ignore
      if (!!window.turnstile) {
        setTurnstileLoaded(true);
        clearInterval(x);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    if (state === "idle" && actionData) {
      // @ts-ignore this is needed because turnstile global var is not typed
      if (window.turnstile) window.turnstile.reset();
    }
  }, [state, actionData, hydState]);

  return (
    <div
      className="min-h-screen relative lg:flex bg-gray-900">
      <div className="hidden lg:block relative lg:flex-grow-[1.618] w-0">
        <div className="absolute inset-0 pricing-bg" />
        <div
          className="relative z-10 flex flex-col justify-center items-start text-gray-300 h-full p-8 md:p-24 lg:p-32">
          <h1 className="text-4xl font-bold text-white leading-[1.35]">
            Join our best customers and find out why people love Rank-Hub's guidance and speed
          </h1>
          {/*<p className="mt-3 text-xl">Join our best customers and find out why people love Rank-Hub's guidance and speed</p>*/}
          <div
            className="flex flex-col xl:flex-row items-center max-w-[70rem] w-full mx-auto mt-16 gap-6 md:gap-24">
            <img src="/testimonial-pictures/fernando2.jpg" alt="Fernando from Kansei" width="400" height="400"
                 className="rounded-full w-32 h-32" />
            <div className="">
              <div className="text-xl text-gray-200 tracking-wide leading-[1.35] relative">
                <p>
                  I was skeptical as I already tried plain GPT for content,
                  which required so much massaging and still missed the mark.
                </p>
                <p className="mt-3">
                  Rank-Hub managed to give me <span
                  className="text-primary-500">content ideas that are actually searched</span> in my niche
                  and write full blog posts that <span
                  className="text-primary-500">I am actually proud to publish</span> with minimal post-editing.
                </p>
                <div className="text-4xl text-gray-400 invisible md:visible">
                  <div
                    className="absolute top-0 left-0 -translate-x-[160%] -translate-y-[60%] font-serif font-bold">&ldquo;</div>
                  <div
                    className="absolute bottom-0 right-0 translate-x-[50%] translate-y-full font-serif font-bold">&rdquo;</div>
                </div>
              </div>
              <p className="mt-6 italic text-lg">
                <a className="underline text-primary-500 underline-offset-2" href="https://twitter.com/JFTucci"
                   target="_blank"
                   rel="noreferrer">
                  Fernando
                </a> from Kansei
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="sm:p-16 p-6 pt-12 lg:flex-grow lg:w-0 flex items-center justify-center bg-white">
        <div className="flex w-full flex-col max-w-sm gap-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight">
              Get {maxTrialCredits} credits for free
            </h1>
            <p className="mt-2">
              Signup to get your free trial, no payment method required.
            </p>
          </div>
          <ValidatedForm validator={validator} method="post" className="">
            <div className="flex flex-col gap-4">
              <Input name="email" label="Email" />
              <Input name="name" label="First Name" />
              <Input name="password" label="Choose Password" type="password" />
              <div className="cf-turnstile" data-sitekey={siteKey}></div>
              <Button submit className="mt-4 flex">
                Sign Up
              </Button>
            </div>

            <p className="text-center text-sm mt-2">
              By clicking Sign up, you agree to our{" "}
              <a
                className="underline underline-offset-4 hover:text-primary"
                href="/legal/terms-of-service"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="underline underline-offset-4 hover:text-primary"
                href="/legal/privacy-policy"
              >
                Privacy Policy
              </a>.
            </p>
            <p className="mt-3 text-sm text-center">
              <Link className="underline underline-offset-4" to="/login">
                Log in to an existing account instead
              </Link>
            </p>
          </ValidatedForm>
        </div>
      </div>
    </div>
  );
}
